import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';

interface OtpVerificationProps {
  identifier: string;
  identifierType: 'EMAIL' | 'PHONE';
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onBack: () => void;
  isLoading: boolean;
  error?: string | null;
  expiresAt?: string;
}

const OTP_LENGTH = 6;
const RESEND_COOLDOWN_SECONDS = 60;
const OTP_DIGIT_POSITIONS = [0, 1, 2, 3, 4, 5] as const;

export function OtpVerification({
  identifier,
  identifierType,
  onVerify,
  onResend,
  onBack,
  isLoading,
  error,
  expiresAt,
}: OtpVerificationProps) {
  const [otp, setOtp] = useState<string[]>(Array(OTP_LENGTH).fill(''));
  const [resendCooldown, setResendCooldown] = useState(0);
  const [expiresIn, setExpiresIn] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState<number | null>(null);
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const inputTypeLabel = identifierType === 'EMAIL' ? 'correo electrónico' : 'teléfono';

  const maskEmail = (rawEmail: string): string => {
    const atIndex = rawEmail.indexOf('@');
    if (atIndex <= 0) return rawEmail;
    const localPart = rawEmail.slice(0, atIndex);
    const domain = rawEmail.slice(atIndex);
    const visibleStart = localPart.slice(0, 2);
    const hiddenCount = Math.max(0, Math.min(localPart.length - 2, 6));
    return `${visibleStart}${'*'.repeat(hiddenCount)}${domain}`;
  };

  const maskPhone = (rawPhone: string): string => {
    const digits = rawPhone.replace(/[^\d+]/g, '');
    if (digits.length <= 4) return digits;
    const start = digits.slice(0, 2);
    const end = digits.slice(-2);
    const middleLength = Math.max(0, Math.min(digits.length - 4, 6));
    return `${start}${'*'.repeat(middleLength)}${end}`;
  };

  const maskedIdentifier =
    identifierType === 'EMAIL' ? maskEmail(identifier) : maskPhone(identifier);

  // Handle expiration countdown
  useEffect(() => {
    if (!expiresAt) return;

    const updateExpiry = () => {
      const now = new Date().getTime();
      const expiry = new Date(expiresAt).getTime();
      const diff = Math.max(0, Math.floor((expiry - now) / 1000));
      setExpiresIn(diff);
    };

    updateExpiry();
    const interval = setInterval(updateExpiry, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleChange = useCallback(
    (index: number, value: string) => {
      // Only allow digits
      const digit = value.replace(/\D/g, '').slice(-1);

      const newOtp = [...otp];
      newOtp[index] = digit;
      setOtp(newOtp);

      // Auto-focus next input
      if (digit && index < OTP_LENGTH - 1) {
        inputRefs.current[index + 1]?.focus();
        setFocusedIndex(index + 1);
      }

      // Auto-submit when complete
      if (digit && newOtp.every(d => d !== '')) {
        onVerify(newOtp.join(''));
      }
    },
    [otp, onVerify]
  );

  const handleKeyDown = useCallback(
    (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Backspace' && !otp[index] && index > 0) {
        inputRefs.current[index - 1]?.focus();
        setFocusedIndex(index - 1);
      }
    },
    [otp]
  );

  const handlePaste = useCallback(
    (e: React.ClipboardEvent) => {
      e.preventDefault();
      const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, OTP_LENGTH);

      if (pastedData.length === OTP_LENGTH) {
        const newOtp = pastedData.split('');
        setOtp(newOtp);
        inputRefs.current[OTP_LENGTH - 1]?.focus();
        setFocusedIndex(OTP_LENGTH - 1);
        onVerify(pastedData);
      }
    },
    [onVerify]
  );

  const handleResend = async () => {
    await onResend();
    setResendCooldown(RESEND_COOLDOWN_SECONDS);
    setOtp(Array(OTP_LENGTH).fill(''));
    inputRefs.current[0]?.focus();
    setFocusedIndex(0);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length === OTP_LENGTH) {
      onVerify(code);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getTimeColor = () => {
    if (expiresIn === null) return 'text-slate-500';
    if (expiresIn <= 60) return 'text-red-500';
    if (expiresIn <= 120) return 'text-amber-500';
    return 'text-emerald-600';
  };

  return (
    <div className='relative flex min-h-screen items-center justify-center overflow-hidden p-4'>
      {/* Animated background gradient */}
      <div className='pointer-events-none absolute inset-0 bg-gradient-to-br from-background via-card to-primary-50 dark:from-background dark:via-card dark:to-background' />
      <div className='pointer-events-none absolute inset-0 overflow-hidden'>
        <div className='absolute -top-60 -right-60 h-96 w-96 animate-pulse-slow rounded-full bg-violet-400/10 blur-3xl' />
        <div className='absolute -bottom-60 -left-60 h-96 w-96 animate-pulse-slow rounded-full bg-cyan-400/10 blur-3xl [animation-delay:1.5s]' />
        <div className='absolute top-1/2 left-1/2 h-96 w-96 -translate-x-1/2 -translate-y-1/2 animate-pulse-slow rounded-full bg-gradient-to-r from-violet-300/20 to-cyan-300/20 blur-3xl [animation-delay:3s]' />
      </div>

      {/* Card with CLEAR border */}
      <div className='relative z-10 w-full max-w-md animate-in fade-in-0 zoom-in-95 duration-500'>
        {/* Header */}
        <div className='text-center'>
          <div className='relative mx-auto mb-6 inline-flex'>
            <div className='absolute inset-0 animate-pulse-slow rounded-3xl bg-gradient-to-r from-primary to-primary-700 blur-xl opacity-40' />
            <div className='relative flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-primary to-primary-700 shadow-2xl shadow-violet-500/30'>
              <svg
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
                className='h-10 w-10 text-white'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z'
                />
              </svg>
            </div>
          </div>

          <span className='inline-block rounded-full border border-violet-200 bg-violet-50 px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300'>
            Verificación en dos pasos
          </span>

          <h1 className='mt-6 text-3xl font-bold text-slate-900 dark:text-white'>
            Ingresa el código
          </h1>

          <p className='mt-3 text-slate-600 dark:text-slate-400'>
            Enviamos un código de 6 dígitos a{' '}
            <span className='font-semibold text-slate-900 dark:text-white'>{maskedIdentifier}</span>
          </p>
        </div>

        {/* Form card - CLEAR BORDER */}
        <div className='mt-8 rounded-3xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-700 dark:bg-slate-900'>
          <form onSubmit={handleSubmit} className='space-y-6'>
            {/* Error alert */}
            {error && (
              <div className='flex items-start gap-3 rounded-2xl border-2 border-red-200 bg-red-50 p-4 dark:border-red-900 dark:bg-red-950/50 animate-in fade-in-0 slide-in-from-top-2 duration-300'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='h-5 w-5 shrink-0 text-red-500'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z'
                  />
                </svg>
                <p className='text-sm font-medium text-red-700 dark:text-red-300'>{error}</p>
              </div>
            )}

            {/* Expiration timer */}
            {expiresIn !== null && (
              <div
                className={`text-center transition-colors duration-300 font-medium ${getTimeColor()}`}
              >
                <div className='flex items-center justify-center gap-2'>
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={1.5}
                    stroke='currentColor'
                    className='h-4 w-4'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M12 6v6h4.5m4.5 0a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                  <span>
                    {expiresIn > 0
                      ? `El código expira en ${formatTime(expiresIn)}`
                      : 'El código ha expirado'}
                  </span>
                </div>
              </div>
            )}

            {/* OTP Inputs - CLEARLY DELIMITED */}
            <div
              className='flex justify-center gap-2 sm:gap-3'
              onPaste={handlePaste}
              role='group'
              aria-describedby='otp-instructions'
            >
              <span id='otp-instructions' className='sr-only'>
                Ingresa el código de 6 dígitos enviado a tu {inputTypeLabel}. Usa las flechas del
                teclado o la pestaña para navegar entre los campos.
              </span>
              {OTP_DIGIT_POSITIONS.map(index => (
                <input
                  key={`otp-digit-${index + 1}`}
                  ref={(el: HTMLInputElement | null) => {
                    inputRefs.current[index] = el;
                  }}
                  type='text'
                  inputMode='numeric'
                  pattern='[0-9]*'
                  maxLength={1}
                  value={otp[index]}
                  onChange={e => handleChange(index, e.target.value)}
                  onKeyDown={e => handleKeyDown(index, e)}
                  onFocus={() => setFocusedIndex(index)}
                  onBlur={() => setFocusedIndex(null)}
                  disabled={isLoading}
                  aria-label={`Dígito ${index + 1} de ${OTP_LENGTH} del código de verificación`}
                  autoComplete={index === 0 ? 'one-time-code' : 'off'}
                  className={`h-16 w-12 text-center text-2xl font-bold rounded-xl border-2 bg-white shadow-sm transition-all duration-200 outline-none focus:ring-2 focus:ring-violet-500/20 dark:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed ${
                    focusedIndex === index
                      ? 'border-violet-500 text-primary ring-2 ring-violet-500/20 dark:text-violet-400'
                      : 'border-slate-300 text-slate-900 hover:border-slate-400 dark:border-slate-600 dark:text-white dark:hover:border-slate-500'
                  }`}
                  autoFocus={index === 0}
                />
              ))}
            </div>

            {/* Submit button - CLEAR CALL TO ACTION */}
            <Button
              type='submit'
              disabled={isLoading || otp.some(d => !d)}
              className={`group relative w-full h-12 rounded-xl text-base font-semibold transition-all duration-200 ${
                isLoading || otp.some(d => !d)
                  ? 'bg-slate-200 text-slate-500'
                  : 'bg-gradient-to-r from-primary to-primary-700 text-white shadow-lg hover:shadow-xl hover:scale-[1.02] active:scale-[0.98]'
              }`}
            >
              {isLoading ? (
                <>
                  <Spinner className='mr-2 h-5 w-5' />
                  Verificando...
                </>
              ) : (
                <>
                  Verificar código
                  <svg
                    xmlns='http://www.w3.org/2000/svg'
                    fill='none'
                    viewBox='0 0 24 24'
                    strokeWidth={2}
                    stroke='currentColor'
                    className='ml-2 h-5 w-5 transition-transform group-hover:translate-x-1'
                  >
                    <path
                      strokeLinecap='round'
                      strokeLinejoin='round'
                      d='M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
                    />
                  </svg>
                </>
              )}
            </Button>

            {/* Action links */}
            <div className='flex flex-col items-center gap-4'>
              <button
                type='button'
                onClick={handleResend}
                disabled={isLoading || resendCooldown > 0}
                className={`group flex items-center gap-2 text-sm font-medium transition-all duration-200 ${
                  isLoading || resendCooldown > 0
                    ? 'cursor-not-allowed text-slate-400'
                    : 'text-primary hover:text-violet-700 active:scale-95 dark:text-violet-400 dark:hover:text-violet-300'
                }`}
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={1.5}
                  stroke='currentColor'
                  className='h-4 w-4 transition-transform group-hover:rotate-180'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0l3.181 3.183a8.25 8.25 0 0013.803-3.7M4.031 9.865a8.25 8.25 0 0113.803-3.7l3.181 3.182m0-4.991v4.99'
                  />
                </svg>
                {resendCooldown > 0 ? `Reenviar en ${resendCooldown}s` : 'Reenviar código'}
              </button>

              <button
                type='button'
                onClick={onBack}
                disabled={isLoading}
                className='group flex items-center gap-1 text-sm text-slate-500 transition-colors hover:text-slate-700 active:scale-95 dark:text-slate-400 dark:hover:text-slate-200 disabled:opacity-50 disabled:cursor-not-allowed'
              >
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  fill='none'
                  viewBox='0 0 24 24'
                  strokeWidth={2}
                  stroke='currentColor'
                  className='h-4 w-4 transition-transform group-hover:-translate-x-1'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    d='M10.5 19.5L3 12m0 0l7.5-7.5M3 12h18'
                  />
                </svg>
                Volver
              </button>
            </div>
          </form>
        </div>
      </div>

      <style>{`
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.3; }
          50% { opacity: 0.6; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 4s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}
